<script setup>
import { computed, ref, watch } from 'vue'
import { Button, FormField, Input, Modal, SegmentedControl, useToast } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { saveModelProvider } from '../../../api/model-capabilities.js'

const props = defineProps({
  open: { type: Boolean, required: true },
  provider: { type: Object, default: null },
})
const emit = defineEmits(['update:open', 'saved'])
const { t } = useI18n()
const toast = useToast()
const saving = ref(false)
const form = ref(emptyProviderForm())

const statusOptions = computed(() => [
  { value: 'enabled', label: t('visualGameAdmin.llm.providers.status.enabled') },
  { value: 'disabled', label: t('visualGameAdmin.llm.providers.status.disabled') },
])
const status = computed({
  get: () => form.value.enabled ? 'enabled' : 'disabled',
  set: value => { form.value.enabled = value === 'enabled' },
})
const invalid = computed(() => form.value.code.trim() === '' || form.value.display_name.trim() === '')

watch(() => props.open, open => {
  if (!open) return
  form.value = props.provider === null
    ? emptyProviderForm()
    : {
        id: Number(props.provider.id),
        code: props.provider.code,
        display_name: props.provider.display_name,
        enabled: props.provider.enabled === 1,
      }
})

function emptyProviderForm() {
  return { id: null, code: '', display_name: '', enabled: true }
}

async function submit() {
  if (invalid.value) return
  saving.value = true
  try {
    const catalog = await saveModelProvider({
      ...form.value,
      code: form.value.code.trim(),
      display_name: form.value.display_name.trim(),
    })
    emit('saved', catalog)
    emit('update:open', false)
    toast.success(t('visualGameAdmin.llm.providers.toast.saved'))
  } catch (error) {
    toast.error(error.message, { duration: 0 })
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <Modal
    :open="open"
    :title="t(`visualGameAdmin.llm.providers.providerForm.${provider ? 'editTitle' : 'createTitle'}`)"
    @update:open="emit('update:open', $event)"
  >
    <form class="model-provider-form" @submit.prevent="submit">
      <FormField :label="t('visualGameAdmin.llm.providers.providerForm.code')" required>
        <Input v-model="form.code" :disabled="provider !== null" />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.providers.providerForm.displayName')" required>
        <Input v-model="form.display_name" />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.providers.basic.enabled')">
        <SegmentedControl v-model="status" :options="statusOptions" />
      </FormField>
    </form>
    <template #footer>
      <Button variant="ghost" @click="emit('update:open', false)">{{ t('visualGameAdmin.common.cancel') }}</Button>
      <Button variant="primary" :loading="saving" :disabled="invalid" @click="submit">
        {{ t('visualGameAdmin.common.save') }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.model-provider-form {
  display: grid;
  gap: var(--drama-space-lg);
}
</style>
